<script lang="ts">

import {
  computed, ref, onMounted, watch,
} from 'vue';
import { Spin, InputSearch } from 'ant-design-vue';
import {
  bool, func, object, string, any,
} from 'vue-types';

import type { OrderSearchParams } from './useTableRouter/seachOrder/functions';
import type { SorterProperties, TableSearchParameters } from './tableProperties';

import Pagination from '@/ui/Pagination/Pagination.vue';
import { type PaginationFragmentFragment, SortOrder } from '@/graphql/generated/graphql';

export interface TableProps<T = TableSearchParameters> {
  pagination?: PaginationFragmentFragment,
  loading?: boolean,
  query: T,
  setQuery: (values: TableSearchParameters, removeAll?: boolean) => void,
  placeholder?: string,
  search?: string,
  setSearch?: (search: string) => void,
  hideSearch?: boolean,
  setSorter?: (sorter?: SorterProperties) => void,
  headerClass?: string,
  autoFocus?: boolean,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  topPagClass?: any,
  showSizeChanger?: boolean,
}
export const props = {
  pagination: object<PaginationFragmentFragment>(),
  loading: Boolean,
  query: object<TableSearchParameters>().isRequired,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setQuery: func<(values: OrderSearchParams<any>, removeAll?: boolean) => void>().isRequired,
  placeholder: string().def('Search'),
  search: string(),
  setSearch: func<(search: string) => void>(),
  hideSearch: bool(),
  setSorter: func<(sorter?: SorterProperties) => void>(),
  headerClass: string(),
  autoFocus: bool(),
  topPagClass: any(),
  autoSearch: bool().def(false),
  showSizeChanger: bool().def(true),
};
</script>
<script setup lang="ts">

const propertes = defineProps({
  pagination: object<PaginationFragmentFragment>(),
  loading: Boolean,
  query: object<TableSearchParameters>().isRequired,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setQuery: func<(values: OrderSearchParams<any>, removeAll?: boolean) => void>().isRequired,
  placeholder: string().def('Search'),
  search: string(),
  setSearch: func<(search: string) => void>(),
  hideSearch: bool(),
  setSorter: func<(sorter?: SorterProperties) => void>(),
  headerClass: string(),
  autoFocus: bool(),
  topPagClass: any(),
  autoSearch: bool().def(false),
  showSizeChanger: bool().def(true),
});

const searchInputRef = ref<InstanceType<typeof InputSearch>>();

// Focus the search input when the component is mounted (only if autoFocus is enabled)
function focusSearchInput() {
  if (searchInputRef.value?.$el) {
    const inputElement = searchInputRef.value?.$el.querySelector('input');
    if (inputElement) {
      inputElement.focus();
    }
  }
}

onMounted(() => {
  if (props.autoFocus) {
    focusSearchInput();
  }
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function onChangeSearch(event: any) {
  if (propertes.setSearch && event) {
    propertes.setSearch(event.target.value);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function handleTableChange(pagination: any, _: any, sorter: any) {
  if (!propertes.setSorter) {
    throw new Error(
      '"setSorter" props is required if you want to use this feature',
    );
  }
  const vars: TableSearchParameters = {
    ...propertes.query,
    search: propertes.search,
    page: pagination?.current,
    first: pagination?.pageSize,
  };
  if (sorter.order && sorter.columnKey) {
    vars.orderBy = [
      {
        order: sorter.order === 'ascend' ? SortOrder.Asc : SortOrder.Desc,
        column: sorter.columnKey,
      },
    ];
    propertes.setSorter(sorter);
  } else {
    propertes.setSorter(undefined);
    vars.orderBy = undefined;
  }
  propertes.setQuery(vars);
}

function onChangePagination(page: number, first: number) {
  propertes.setQuery({
    ...props.query,
    page,
    first,
    search: propertes.search,
  });
}

function onSearch(search: string) {
  if (propertes.setSorter) {
    propertes.setSorter(undefined);
  }
  propertes.setQuery({
    ...props.query,
    page: 1,
    first: propertes.pagination?.perPage,
    search,
  });
}

const displayPagination = computed(() => true);

watch(() => propertes.search, (search) => {
  if (propertes.autoSearch && propertes.search && search && search.length >= 3) {
    onSearch(search);
  }
});

defineExpose({
  focusSearchInput,
});
</script>

<template>
  <div :class="headerClass" data-testid="table-header">
    <slot name="header_left" />
    <slot name="header_center">
      <InputSearch
        v-if="setSearch && !hideSearch"
        ref="searchInputRef"
        :placeholder="placeholder"
        :value="search"
        :enter-button="true"
        class="mb-4"
        data-testid="search-input"
        @change="onChangeSearch"
        @search="onSearch"
      >
        <template #suffix>
          <slot name="input_search_suffix" />
        </template>
      </InputSearch>
    </slot>
    <slot name="header_right" />
  </div>
  <slot name="subheader" />
  <div :class="topPagClass">
    <slot name="top_pag_left" />
    <div class="flex space-x-4 justify-end">
      <Pagination
        v-if="pagination && displayPagination"
        :on-change="onChangePagination"
        :pagination="pagination"
        :show-total="true"
        :show-size-changer="showSizeChanger"
        size="small"
        class="text-right"
      />
      <slot name="top_pag_right" />
    </div>
  </div>
  <Spin :spinning="loading">
    <div class="grid grid-cols-1">
      <div class="w-full overflow-x-auto relative">
        <slot v-bind="{ handleTableChange }" />
      </div>
    </div>
  </Spin>
  <Pagination
    v-if="displayPagination && pagination"
    :on-change="onChangePagination"
    :pagination="pagination"
    class="mt-4 text-center"
  />
</template>
